/**
 * RunsPage — run list, live event tail, and stop/pause controls.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. The run list and the
 * event stream are the server's; this page polls `fetchRunEvents` while a run
 * is expanded — polling, not a decision: what appears in the tail is exactly
 * what the server returned, newest fetch replacing the older one. Stop and
 * pause are POSTs the server owns; buttons are disabled while a request is
 * in flight. The `origin` badge is rendered verbatim so a viewer always sees
 * where a run came from.
 *
 * UNAVAILABLE IS HONEST: `/v1/operations/runs` is a documented contract, not
 * mounted at this base ref; a 404/503 renders an explicit Unavailable state.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  OperationsApiError,
  fetchRunEvents,
  fetchRuns,
  pauseRun,
  stopRun,
} from '../../api/operations';

const POLL_MS = 4000;

function RunsPage({ campaignId }) {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState('');
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await fetchRuns(campaignId);
      setRuns(response.runs || []);
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setRuns([]);
        setError('unavailable');
      } else {
        setRuns([]);
        setError(err instanceof Error ? err.message : 'the run list could not be loaded');
      }
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live event tail: polling while exactly one run is expanded. The fetched
  // events replace the old list; the page never merges or invents entries.
  useEffect(() => {
    if (!expanded) {
      setEvents([]);
      return undefined;
    }
    let active = true;
    const tick = async () => {
      try {
        const response = await fetchRunEvents(expanded);
        if (active) setEvents(response.events || []);
      } catch {
        // A transient poll failure is not fatal; the next tick retries.
      }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [expanded]);

  const act = useCallback(
    async (runId, verb) => {
      setBusy(runId + ':' + verb);
      try {
        if (verb === 'stop') await stopRun(runId);
        else if (verb === 'pause') await pauseRun(runId);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'the action failed');
      } finally {
        setBusy('');
      }
    },
    [load],
  );

  if (error === 'unavailable') {
    return (
      <section>
        <h1>Runs</h1>
        <p className="unavailable">
          Unavailable — the operations backend is not mounted for this deployment, so no
          run can be listed. Nothing here is fabricated.
        </p>
      </section>
    );
  }

  if (runs === null) {
    return (
      <section>
        <h1>Runs</h1>
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Runs</h1>
      {error && error !== 'unavailable' ? <p className="error">{error}</p> : null}
      {runs.length === 0 ? <p>No runs were reported by the server.</p> : null}
      <table>
        <thead>
          <tr>
            <th>Run</th>
            <th>Status</th>
            <th>Origin</th>
            <th>Started</th>
            <th>Controls</th>
            <th>Events</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.run_id}>
              <td>{run.run_id}</td>
              <td>{run.status}</td>
              <td><span className="badge">{run.origin}</span></td>
              <td>{run.started_at}</td>
              <td>
                <button type="button" disabled={busy !== ''} onClick={() => act(run.run_id, 'pause')}>
                  Pause
                </button>
                <button type="button" disabled={busy !== ''} onClick={() => act(run.run_id, 'stop')}>
                  Stop
                </button>
              </td>
              <td>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === run.run_id ? '' : run.run_id)}
                >
                  {expanded === run.run_id ? 'Hide events' : 'Show events'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {expanded ? (
        <div className="event-tail">
          <h2>Live events — run {expanded}</h2>
          {events.length === 0 ? (
            <p>No events have been returned for this run yet.</p>
          ) : (
            <ul>
              {events.map((event) => (
                <li key={event.event_id} className={`event event-${event.level}`}>
                  <span className="event-at">{event.at}</span>{' '}
                  <span className="event-message">{event.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default RunsPage;
